/**
 * PostHog Verification Utility
 * Safe in Node (getPostHogInstance returns null when window is undefined).
 */

interface PostHogInstance {
  captureException?: (error: Error, properties?: Record<string, unknown>) => void;
  capture?: (event: string, properties?: Record<string, unknown>) => void;
  isFeatureEnabled?: (flag: string) => boolean | undefined;
  get_distinct_id?: () => string | undefined;
  has_opted_in_capturing?: () => boolean;
  has_opted_out_capturing?: () => boolean;
}

let registeredPostHogInstance: PostHogInstance | null = null;

export function registerPostHogForVerification(posthog: PostHogInstance): void {
  registeredPostHogInstance = posthog;
}

export function getPostHogInstance(): PostHogInstance | null {
  if (registeredPostHogInstance) return registeredPostHogInstance;
  if (typeof window !== 'undefined') {
    const w = window as unknown as { posthog?: PostHogInstance };
    if (w.posthog && (typeof w.posthog.capture === 'function' || typeof w.posthog.captureException === 'function')) {
      return w.posthog;
    }
  }
  return null;
}

export function verifyPostHogHealth(): {
  isAvailable: boolean;
  canCapture: boolean;
  canCaptureException: boolean;
  distinctId?: string;
  hasOptedIn?: boolean;
  hasOptedOut?: boolean;
  error?: string;
} {
  const posthog = getPostHogInstance();
  if (!posthog) {
    return { isAvailable: false, canCapture: false, canCaptureException: false, error: 'PostHog instance not found' };
  }
  const canCapture = typeof posthog.capture === 'function';
  const canCaptureException = typeof posthog.captureException === 'function';
  const distinctId = typeof posthog.get_distinct_id === 'function' ? posthog.get_distinct_id() : undefined;
  const hasOptedIn = typeof posthog.has_opted_in_capturing === 'function' ? posthog.has_opted_in_capturing() : undefined;
  const hasOptedOut = typeof posthog.has_opted_out_capturing === 'function' ? posthog.has_opted_out_capturing() : undefined;
  return {
    isAvailable: true,
    canCapture,
    canCaptureException,
    distinctId,
    hasOptedIn,
    hasOptedOut,
    error: !canCapture && !canCaptureException ? 'PostHog instance found but missing capture methods' : undefined,
  };
}

export function testPostHogCapture(): Promise<boolean> {
  return new Promise((resolve) => {
    const posthog = getPostHogInstance();
    if (!posthog || typeof posthog.capture !== 'function') {
      resolve(false);
      return;
    }
    try {
      posthog.capture('posthog_health_check', { timestamp: new Date().toISOString(), test: true });
      resolve(true);
    } catch {
      resolve(false);
    }
  });
}

export function testPostHogExceptionCapture(): Promise<boolean> {
  return new Promise((resolve) => {
    const posthog = getPostHogInstance();
    if (!posthog || typeof posthog.captureException !== 'function') {
      resolve(false);
      return;
    }
    try {
      const testError = new Error('PostHog health check test error');
      testError.name = 'PostHogHealthCheckTest';
      posthog.captureException(testError, { test: true, timestamp: new Date().toISOString() });
      resolve(true);
    } catch {
      resolve(false);
    }
  });
}

/** PostHog event properties must be string | number | boolean (no null/objects). */
export function verifyPostHogContractTelemetry(
  properties: Record<string, unknown>,
): { ok: true } | { ok: false; error: string } {
  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined) {
      return { ok: false, error: `${key} is nullish` };
    }
    const t = typeof value;
    if (t !== 'string' && t !== 'number' && t !== 'boolean') {
      return { ok: false, error: `${key} has invalid type ${t}` };
    }
  }
  return { ok: true };
}

/** Telemetry shape for audited API contract routes (matches transport PostHog capture). */
export function buildApiContractTelemetryProperties(
  method: string,
  path: string,
  extra?: Record<string, string | number | boolean>,
): Record<string, string | number | boolean> {
  return {
    api_method: method,
    api_path: path.split('?')[0],
    ...extra,
  };
}

export function logPostHogHealth(): void {
  const health = verifyPostHogHealth();
  if (health.isAvailable) {
    console.log('[PostHog] Health Check:', {
      status: 'Available',
      canCapture: health.canCapture,
      canCaptureException: health.canCaptureException,
      distinctId: health.distinctId,
      hasOptedIn: health.hasOptedIn,
      hasOptedOut: health.hasOptedOut,
    });
  } else {
    console.warn('[PostHog] Health Check Failed:', { status: 'Unavailable', error: health.error });
  }
}
